import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  type BuilderContext,
  DEFAULT_MODEL_ID,
  SUPPORTED_MODELS,
  type SupportedModelId,
  type ToolActivity,
} from "common/utils/chatAgent/types";
import { FormStructure } from "common/utils/formStructure";
import { colors, spacing } from "../../../stitches.config";
import { track } from "../../../utils/tracking";
import { FlexRow } from "../../Common/__styles__/Layout";
import { fontSizes } from "../../Common/__styles__/Typography";
import {
  ChatContent,
  InputContainer,
  PanelContainer,
  ScrollableContent,
} from "./__styles__/ChatPanel";
import AssistantIntro, { AssistantSuggestion } from "./AssistantIntro";
import ChatHeader from "./ChatHeader";
import ChatHistory from "./ChatHistory";
import ChatInput from "./ChatInput";
import { useChatAgent } from "./hooks/useChatAgent";

export type ChatPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  submissionTypeVersionId?: string;
  // V1 payload (builderState)
  formData?: Record<string, any>;
  // V2 payload (FormStructure)
  formStructure?: FormStructure;
  onFormUpdate?: (updates: any) => void;
  api?: string;
  suggestions?: AssistantSuggestion[];
  // Show model picker (V2 only)
  showModelPicker?: boolean;
  ToolActivityCardComponent?: React.ComponentType<{ activity: ToolActivity }>;
  thinkingPhrases?: string[];
  workingPhrases?: string[];
  // V2: Context for server-side resolution
  builderContext?: BuilderContext;
};

const MODEL_OPTIONS = Object.values(SUPPORTED_MODELS);

type ModelSelectorProps = {
  value: SupportedModelId;
  onChange: (modelId: SupportedModelId) => void;
  disabled?: boolean;
};

const ModelSelector = ({ value, onChange, disabled }: ModelSelectorProps) => (
  <select
    value={value}
    onChange={e => onChange(e.target.value as SupportedModelId)}
    disabled={disabled}
    style={{
      padding: "4px 8px",
      borderRadius: "4px",
      border: `1px solid ${colors.grey300.value}`,
      backgroundColor: disabled ? colors.bgUiList.value : colors.bgUiList.value,
      fontSize: fontSizes.body2,
      color:
        disabled ? colors.contentDisabled.value : colors.contentPrimary.value,
      cursor: disabled ? "not-allowed" : "pointer",
      outline: "none",
      opacity: disabled ? 0.7 : 1,
    }}
  >
    {MODEL_OPTIONS.map(model => (
      <option key={model.id} value={model.id}>
        {model.name}
      </option>
    ))}
  </select>
);

const ChatPanel = ({
  isOpen,
  onClose,
  submissionTypeVersionId,
  formData,
  formStructure,
  onFormUpdate,
  api,
  suggestions,
  showModelPicker = false,
  ToolActivityCardComponent,
  thinkingPhrases,
  workingPhrases,
  builderContext,
}: ChatPanelProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [selectedModel, setSelectedModel] =
    useState<SupportedModelId>(DEFAULT_MODEL_ID);

  const {
    messages,
    input,
    handleSubmit,
    isLoading,
    clearChat,
    setInput,
    sendMessage,
    files,
    addFiles,
    removeFile,
    audioRecordingStatus,
    recordingTime,
    isTranscribing,
    onStartRecording,
    onStopRecording,
    onCancelRecording,
  } = useChatAgent({
    api,
    submissionTypeVersionId,
    formData,
    formStructure,
    onFormUpdate,
    model: showModelPicker ? selectedModel : undefined,
    builderContext,
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      setInput(suggestion);
      void sendMessage();
    },
    [setInput, sendMessage],
  );

  // Track when panel opens
  useEffect(() => {
    if (isOpen) {
      track("Builder assistant chat opened");
    }
  }, [isOpen]);

  // Reset chat when panel closes
  useEffect(() => {
    if (!isOpen) {
      clearChat();
    }
  }, [isOpen, clearChat]);

  const handleMessageSubmit = useCallback(
    (e: React.FormEvent) => {
      track("Builder assistant message sent", { model: selectedModel });
      handleSubmit(e);
    },
    [handleSubmit, selectedModel],
  );

  const handleModelChange = useCallback((modelId: SupportedModelId) => {
    track("Builder assistant model changed", { model: modelId });
    setSelectedModel(modelId);
  }, []);

  return (
    <PanelContainer isOpen={isOpen}>
      <ChatHeader onClose={onClose} />
      <ChatContent>
        <ScrollableContent>
          <AssistantIntro
            onSuggestionClick={handleSuggestionClick}
            suggestions={suggestions}
          />
          {messages.length > 0 && (
            <ChatHistory
              messages={messages}
              isLoading={isLoading}
              ToolActivityCardComponent={ToolActivityCardComponent}
              thinkingPhrases={thinkingPhrases}
              workingPhrases={workingPhrases}
            />
          )}
          <div ref={messagesEndRef} />
        </ScrollableContent>
        <InputContainer>
          <ChatInput
            value={input}
            onChange={e => setInput(e.target.value)}
            onSubmit={handleMessageSubmit}
            disabled={isLoading}
            placeholder="Let's get started..."
            files={files}
            onFilesAdd={addFiles}
            onFileRemove={removeFile}
            audioRecordingStatus={audioRecordingStatus}
            recordingTime={recordingTime}
            isTranscribing={isTranscribing}
            onStartRecording={onStartRecording}
            onStopRecording={onStopRecording}
            onCancelRecording={onCancelRecording}
          />
          {showModelPicker && (
            <FlexRow css={{ paddingTop: spacing.s.value }}>
              <ModelSelector
                value={selectedModel}
                onChange={handleModelChange}
                disabled={messages.length > 0}
              />
            </FlexRow>
          )}
        </InputContainer>
      </ChatContent>
    </PanelContainer>
  );
};

export default ChatPanel;
